import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Chart } from "react-google-charts";
import image from '../../../assets/css/images/nahlasene.jpg';
import { State } from '../../../interfaces/europe/interfaces';
import './TEuropeAccidentsMap.css';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { GET_ALL_STATES } from '../../../interfaces/europe/Constants';
import HeaderCarousel from '../../../components/HeaderCarousel/HeaderCarousel';
import PageForm from '../../../components/PageHeader/PageForm';
import { useTranslation } from "react-i18next";
import { Box, CircularProgress } from '@mui/material';

export interface EuropeAccidentDTO {
    id: number;
    stateId: number;
    year: number;
    entryType: number;
    value: number;
    codeState?: string;
}

export default function TEuropeAccidentsGraph() {

    const { t: tEurope } = useTranslation("europe");
    const { t } = useTranslation("traffic");

    interface AccidentType {
        value: number;
        label: string;
    }

    const accidentTypes: AccidentType[] = [
        { value: 0, label: "EuropeMap.subTitle.accidents" },
        { value: 1, label: "EuropeMap.subTitle.casualties" },
        { value: 2, label: "EuropeMap.subTitle.casualties-peds" },
        { value: 3, label: "EuropeMap.subTitle.casualties-cars" },
        { value: 4, label: "EuropeMap.subTitle.casualties-cyclists" }
    ];

    const [states, setStates] = useState<State[]>([]);
    const [selectedState, setSelectedState] = useState<number | null>(null);
    const [selectedEntryType, setSelectedEntryType] = useState<number>(0);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [chartData, setChartData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [allData, setAllData] = useState<EuropeAccidentDTO[]>([]);

    // Fetch states on component mount
    useEffect(() => {
        axios
            .get(process.env.REACT_APP_API_URL_EU + GET_ALL_STATES)
            .then((response) => {
                setStates(response.data);
                if (response.data.length > 0) {
                    setSelectedState(response.data[0].id);
                }
            })
            .catch((error) => console.error("Error fetching states:", error));
    }, []);

    // Fetch data when state or entry type changes
    useEffect(() => {
        if (selectedState === null) return;

        setIsLoading(true);
        axios
            .get(
                process.env.REACT_APP_API_URL_EU +
                `/EuropeAccident/${selectedEntryType}/${selectedState}`
            )
            .then((response) => {
                const data: EuropeAccidentDTO[] = response.data;
                setAllData(data);
                
                // Extract unique years and sort descending
                const uniqueYears = Array.from(new Set(data.map(d => d.year))).sort((a, b) => b - a);
                setAvailableYears(uniqueYears);
                
                // Auto-select all years
                setSelectedYears(uniqueYears);
            })
            .catch((error) => {
                console.error("Error fetching accidents:", error);
                setChartData([]);
                setAllData([]);
            })
            .finally(() => setIsLoading(false));
    }, [selectedState, selectedEntryType]);

    // Build chart when selected years change
    useEffect(() => {
        if (selectedYears.length > 0) {
            buildChartData();
        } else {
            setChartData([]);
        }
    }, [selectedYears]);

    const buildChartData = () => {
        // Filter data for the selected years
        const filteredData = allData.filter(d => selectedYears.includes(d.year));

        // Group by year and sum the values
        const yearlyTotals: Record<number, number> = {};
        filteredData.forEach(record => {
            if (!yearlyTotals[record.year]) {
                yearlyTotals[record.year] = 0;
            }
            yearlyTotals[record.year] += record.value;
        });

        // Sort years and build chart data
        const sortedYears = Object.keys(yearlyTotals)
            .map(Number)
            .sort((a, b) => a - b);

        // Build Google Charts data
        const chartHeader = ["Year", "Count"];
        const chartRows = sortedYears.map(year => [
            year.toString(),
            yearlyTotals[year]
        ]);

        setChartData([chartHeader, ...chartRows]);
    };

    const handleStateChange = (event: SelectChangeEvent<number>) => {
        setSelectedState(event.target.value as number);
    };

    const handleEntryTypeChange = (event: SelectChangeEvent<number>) => {
        setSelectedEntryType(event.target.value as number);
    };

    const handleYearChange = (year: number, checked: boolean) => {
        if (checked) {
            setSelectedYears([...selectedYears, year]);
        } else {
            setSelectedYears(selectedYears.filter(y => y !== year));
        }
    };

    const selectedStateName = states.find(s => s.id === selectedState)?.name || "Select a state";

    const options = {
        title: `${selectedStateName} - ${t(accidentTypes.find(at => at.value === selectedEntryType)?.label || "")}`,
        legend: { position: "bottom" },
        hAxis: {
            title: t("Graph.roky") || "Year"
        },
        vAxis: {
            title: "Count"
        },
        colors: ["#4CAF50"]
    };

    return (
        <PageForm label={t("EuropeMap.title")} image={image}>
            <div className="box-12">
                <HeaderCarousel name={t("EuropeMap.graphTitle")} image={image} />

                <div className="py-2" style={{ display: "flex", gap: "20px", marginBottom: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
                    {/* State Selection */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="state-select-label">{t("Search.selectCountry") || "Select Country"}</InputLabel>
                        <Select
                            labelId="state-select-label"
                            id="state-select"
                            value={selectedState || ""}
                            onChange={handleStateChange}
                            label={t("Search.selectCountry") || "Select Country"}
                        >
                            {states.map(state => (
                                <MenuItem key={state.id} value={state.id}>
                                    {state.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Entry Type Selection */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="entry-type-select-label">{t("Search.selectType") || "Select Type"}</InputLabel>
                        <Select
                            labelId="entry-type-select-label"
                            id="entry-type-select"
                            value={selectedEntryType}
                            onChange={handleEntryTypeChange}
                            label={t("Search.selectType") || "Select Type"}
                        >
                            {accidentTypes.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                    {t(type.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Year Selection */}
                    {availableYears.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
                            <span style={{ fontWeight: "bold", minWidth: "60px" }}>{t("Graph.roky")}:</span>
                            {availableYears.map(year => (
                                <FormControlLabel
                                    key={year}
                                    control={
                                        <Checkbox
                                            checked={selectedYears.includes(year)}
                                            onChange={(event) => handleYearChange(year, event.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={year}
                                />
                            ))}
                        </Box>
                    )}
                </div>

                {/* Loading Indicator */}
                {isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Chart */}
                {!isLoading && chartData.length > 1 && (
                    <Box sx={{ marginTop: "30px" }}>
                        <Chart
                            chartType="BarChart"
                            data={chartData}
                            options={options}
                            width="100%"
                            height="400px"
                        />
                    </Box>
                )}

                {!isLoading && chartData.length <= 1 && (
                    <Box sx={{ textAlign: "center", padding: "20px", color: "#999" }}>
                        {t("Graph.error") || "No data available"}
                    </Box>
                )}
            </div>
        </PageForm>
    );
}
